import {CloudObject, Query, QueryResult, registerBrick, ErrorFlow, GlobalProperties} from 'olympe';
import {ReactBrick, useProperty, getLogger, jsonToSxProps, cssToSxProps, ifNotNull, ifNotTransparent} from '@olympeio/core'
import React, {useRef} from "react";
import {Chart} from 'chart.js';
import {combineLatest, debounceTime, merge, of} from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { parseJSON } from "./utils/utils";
import {merge as lodashDeepMerge} from 'lodash';

import Box from '@mui/material/Box';
import CartesianConfig from "./utils/CartesianConfig";

export default class CartesianChart extends ReactBrick {

    /**
     * Subscribe to the config datasets to read the configuration of the brick
     * Then subscribe to the data as well to fill the chart js object with the data
     * (This last step is done in the processData method)
     * Finally set the context variable 'graph data' with the chart js dataset object
     * 
     * By overriding init() we can set the context variable used in the rendering of the React component
     * and we don't need to override the setupExecution() since Visual Component are rendered only once
     * and then react takes care of further updates
     *
     * @override
     */
    init($) {
        this.logger = getLogger('CartesianChart');
        let modelName = '';
        $.observe('config', false).pipe(
            // Read the instances of the config type provided
            switchMap((configType) => {
                if (configType?.query().followRecursively(CloudObject.extendRel).executeFromCache().has('018d3057fd26709b9685')) {
                    modelName = configType.name();
                    return configType ? Query.instancesOf(configType, false).observe($) : of(QueryResult.empty());
                } else {
                    if (!!configType) {
                        if (!$.get(GlobalProperties.EDITION, true)) {
                            throw ErrorFlow.create('The config provided to the chart should extend the Cartesian Chart Config object', 1);
                        } else {
                            this.logger.error('The config provided to the chart should extend the Cartesian Chart Config object');
                        }
                    }
                    return of(QueryResult.empty());
                }
            }),

            // Put the trigger to redisplay anytime any config prop change, also when the user specify manually to refresh the chart
            // When we switch axis, we must compute the data differently, so we listen to that property as well
            switchMap((configs) => {
                if(configs.size() === 0 && modelName.trim() !== '') {
                    this.logger.warn(`The config "${modelName}" does not have any instance`);
                }
                return merge(
                    combineLatest(configs.map((config) => config.observeProperties($).pipe(map(() => config)))),
                    $.observe('Refresh', false).pipe(map(() => configs.toArray())),
                    $.observe('switch axis', false).pipe(map(() => configs.toArray())),
                    $.observe('font family', false).pipe(map(() => configs.toArray())),
                    $.observe('font size', false).pipe(map(() => configs.toArray()))
                );
            }),

            // Add data to the configs to create the datasets
            switchMap((configs) => {
                return $.observe('data', false).pipe(
                    debounceTime(200), // Debounce to avoid multiple rendering on setup or if the data updates very quickly
                    map((data) => this.processData($, data, configs)),
                );
            })
        ).subscribe((datasets) => {
            $.set('graph data', datasets);
        });
    }



    /**
     * Mix the data and the config into the dataset structure we send to chart js
     *
     * @private
     * @param {BrickContext} $
     * @param {Map | Array} data
     * @param {Object} dataConfigs
     * @return {Object}
     */
    processData($, data, dataConfigs) {
        const result = {
            labels: [],
            datasets: []
        };

        // If no config, get a default one, for simple usage
        if (dataConfigs.length === 0) {
            dataConfigs.push(CloudObject.get(CartesianConfig.DEFAULT_INSTANCE_TAG));
        }

        // Transform our config object to a chart js comprehensive config object
        for (let j = 0; j < dataConfigs.length; j++) {
            const config = {
                name: dataConfigs[j].getName(),
                borderColor: dataConfigs[j].getColor()?.toHexString(),
                colorPath: dataConfigs[j].getColorPath(),
                label: dataConfigs[j].getLegend(),
                type: dataConfigs[j].getType(),
                xPath: dataConfigs[j].getXPath(),
                yPath: dataConfigs[j].getYPath(),
                rPath: dataConfigs[j].getRPath(),
                yAxisID: dataConfigs[j].getAxis() === 'First (left)' ? 'y1' : 'y2',
                spanGaps: true,
                axis: 'y',
                order: dataConfigs[j].getOrder(),
                data: [],
                backgroundColor: [],
                datalabels: {
                    anchor: dataConfigs[j].getType() === 'bar' ? 'center' : 'end',
                    align: dataConfigs[j].getType() === 'bar' ? 'center' : 'end',
                    backgroundColor: dataConfigs[j].getType() === 'bar' ? '#00000000' : dataConfigs[j].getColor()?.toHexString(),
                    borderRadius: 5,
                    color: '#FFFFFF',
                    display: dataConfigs[j].getDisplayDataLabels(),
                    font: {
                        family: $.get('font family'),
                        size: $.get('font size')
                    }
                }
            };

            // An area is not a chart in itself, it's a configuration of the line chart
            if (config.type === 'area') {
                config.type = 'line';
                config.fill = 'origin';
                config.tension = 0.3; // Can be overridden by the config json prop if needed
            }

            // Do the sanity checks over the data before injecting it into the chart config object
            if (data) {
                let dataList;
                if (dataConfigs.length === 1 && (Array.isArray(data) || data instanceof QueryResult)) {
                    // Support for a simple list for the sake of simplicity when we don't have multiple dataset
                    dataList = data;
                } else if (data instanceof Map) {
                    dataList = data.get(dataConfigs[j].getName());
                } else if (data instanceof Object) {
                    dataList = data[dataConfigs[j].getName()];
                } else {
                    this.logger.warn('The provided data is neither a map/object nor an array/QueryResult. Can\'t display the chart');
                }

                if (!dataList) {
                    this.logger.warn(`No data has been found for the configured name "${dataConfigs[j].getName()}"`);
                } else {
                    if (!Array.isArray(dataList) && !dataList instanceof QueryResult) {
                        this.logger.warn(`The data found for the configured name "${dataConfigs[j].getName()}" is not an array nor a QueryResult. It cannot be displayed on the chart.`);
                    } else {
                        if (dataList instanceof QueryResult) {
                            dataList = dataList.toArray();
                        }
      
                        // Injecting the data in the chart js config object
                        for (let i = 0; i < dataList.length; i++) {
                            let item = dataList[i];
                            if (item instanceof CloudObject) {
                                item = item.toJSON();
                            }
                            const xValue = item[config.xPath];
                            const yValue = item[config.yPath];
                            const rValue = item[config.rPath];
                      
                            if(xValue === undefined) {
                                this.logger.warn(`xPath (${config.xPath}) is not found in the data: ${item}`);
                            }

                            if(yValue === undefined) {
                                this.logger.warn(`yPath (${config.yPath}) is not found in the data: ${item}`);
                            }

                            if(rValue === undefined && config.type === 'bubble') {
                                this.logger.warn(`rPath (${config.rPath}) is not found in the data: ${item}`);
                            }

                            // The scatter & bubble chart have a specificity: they need another data format & don't use the global 'labels' variable
                            if (config.type === 'scatter' || config.type === 'bubble') {
                                if (typeof xValue !== 'number' || typeof yValue !== 'number' || (typeof rValue !== 'undefined' && typeof rValue !== 'number')) {
                                    this.logger.warn(`You're trying to use a scatter/bubble chart without providing a numeric coordinates. `+
                                        `Value received: x:${xValue}, y:${yValue}, r:${rValue} `);
                                }
                            }

                            // When we invert the axis, we have to switch the data, to match the axis
                            if ($.get('switch axis')) {
                                config.data.push({y: xValue, x: yValue, r: rValue, label: yValue ?? ''});
                            } else {
                                config.data.push({x: xValue, y: yValue, r: rValue, label: yValue ?? ''});
                            }

                            // Only add the backgroundColor prop if we have colorPath, otherwise default on the color set in config
                            const colorValue = config.colorPath ? item[config.colorPath] : null;
                            if (colorValue !== null) {
                                config.backgroundColor.push(colorValue);
                            } else {
                                config.backgroundColor.push(config.borderColor);
                            }
                        }
                    }
                }
            }

            // Setting the dataset while taking care of having the customConfig overriding all the others props
            const customConfig = parseJSON(
                dataConfigs[j].getConfigJSON(),
                this.logger,
                'The JSON object provided to configure the cartesian chart (in the dataset config) is not valid'
            );
            result.datasets[j] = lodashDeepMerge({...config}, customConfig)

            // Cleaning the value chart js don't care for
            delete result.datasets[j].xPath;
            delete result.datasets[j].yPath;
            delete result.datasets[j].rPath;
            delete result.datasets[j].colorPath;
        }
        return result;
    }



    /**
     * Drawing the component itself
     *
     * @override
     * @param {BrickContext} $
     */
    static getReactComponent($){
        return () => {
            const canvasRef = useRef(null);
            const [chart, setChart] = React.useState(null);

            const data = useProperty($, 'graph data', true);
            const title = useProperty($, 'title', true);
            const leftYLabel = useProperty($, 'left Y label', true);
            const rightYLabel = useProperty($, 'right Y label', true);
            const xStack = useProperty($, 'x stack', true);
            const yStack = useProperty($, 'y stack', true);
            const xAxisType = useProperty($, 'x axis type', true);
            const switchAxis = useProperty($, 'switch axis', true);
            const legendPosition = useProperty($, 'legend position', true);
            const tooltipMode = useProperty($, 'tooltip mode', true);
            const configJson = useProperty($, 'config JSON', true);
            const animation = useProperty($, 'animation', true);
            const fontSize = useProperty($, 'font size', true);
            const fontFamily = useProperty($, 'font family', true);
            const showGrid = useProperty($, 'show grid', true);

            // Nice effects that doesn't require to re-draw the whole chart
            React.useEffect(() => {
                if (chart) {
                    chart.options.scales.y1.title.display = leftYLabel !== '';
                    chart.options.scales.y1.title.text = leftYLabel;
                    chart.options.scales.y2.title.display = rightYLabel !== '';
                    chart.options.scales.y2.title.text = rightYLabel;
                    chart.options.scales.x.stacked = xStack;
                    chart.options.scales.y1.stacked = yStack;
                    chart.options.scales.y2.stacked = yStack;
                    chart.update();
                }
            }, [leftYLabel, rightYLabel, xStack, yStack]);

           
            // Build the whole configuration object if there is any change
            React.useEffect(()=>{
                // check if props are ready
                const canvas = canvasRef.current
                if (!canvas || !data) return;

                const handleScaleWidthUpdate = (scale) => {
                    const configYAxisID = scale.chart._metasets[0].yAxisID;
                    if (scale.id === configYAxisID && switchAxis && scale?.ticks ) {
                        const longestLabel = Math.max(...scale.ticks.map(item => item.label.length));
                        scale.width = longestLabel * fontSize * 0.6 + 35;
                    }
                }
                const config = {
                    data: data,
                    options: {
                        scales: {
                            x: {
                                type: switchAxis ? 'linear' : xAxisType,
                                stacked: xStack,
                                ticks: {
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    }
                                },
                                grid: {
                                    display: showGrid
                                }
                            },
                            y1: {
                                title: {
                                    display: leftYLabel !== '',
                                    text: leftYLabel,
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    }
                                },
                                display: 'auto',
                                stacked: yStack,
                                type: switchAxis ? xAxisType : 'linear',
                                ticks: {
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    }
                                },
                                afterUpdate: handleScaleWidthUpdate,
                                grid: {
                                    display: showGrid
                                }
                            },
                            y2: {
                                title: {
                                    display: rightYLabel !== '',
                                    text: rightYLabel,
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    }
                                },
                                position: 'right',
                                display: 'auto',
                                stacked: yStack,
                                type: switchAxis ? xAxisType : 'linear',
                                ticks: {
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    }
                                },
                                afterUpdate: handleScaleWidthUpdate,
                                grid: {
                                    display: showGrid
                                }
                            },      
                        },
                        indexAxis: switchAxis ? 'y' : 'x',
                        interaction: { mode: 'index' },
                        maintainAspectRatio: false,
                        plugins: {
                            title: {
                                display: title !== null && title !== undefined && title !== '',
                                text: String(title),
                                font: {
                                    size: fontSize,
                                    family: fontFamily
                                }
                            },
                            legend: {
                                display: legendPosition !== 'none',
                                position: legendPosition,
                                labels: {
                                    font: {
                                        size: fontSize,
                                        family: fontFamily
                                    }
                                }
                            },
                            tooltip: {
                                enabled: tooltipMode !== 'none',
                                mode: tooltipMode
                            }
                        },
                        animation: animation,
                    },
                };

                // Override the config object we built, so the dev can customize as he wish
                const customConfig = parseJSON(
                    configJson,
                    getLogger('CartesianChart'),
                    'The JSON object provided to configure the cartesian chart is not valid'
                );
                lodashDeepMerge(config, customConfig)

                // When we want to draw the chart again, we need to destroy it first
                if(chart){
                    chart.destroy();
                }
                setChart(new Chart(canvas, config));
            }, [data, title, xAxisType, legendPosition, tooltipMode, configJson, animation, showGrid]);

            // Applying the default params of the component over the div
            return <Box
                // Properties + UI
                component={'div'}
                sx={{
                    width: 1,
                    height: 1,
                    ...ifNotTransparent('borderColor', useProperty($, 'Border Color')),
                    ...ifNotNull('borderRadius', `${useProperty($, 'Border Radius')}px`, useProperty($, 'Border Radius')),
                    borderWidth: useProperty($, 'Border Width'),
                    borderStyle: 'solid',
                    boxSizing: 'border-box',
                    ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color')),
                    ...cssToSxProps(useProperty($, 'CSS Property')),
                    ...jsonToSxProps(useProperty($, 'MUI sx [json]'))
                }}
                onClick={CartesianChart.clickHandler.bind(this, $, chart)}
            >
                <canvas ref={canvasRef}></canvas>
            </Box>
        }
    }



    /**
     * Triggers the on item click if the function is defined. if on item click is triggered, on click is not.
     * If there is no on item click function defined, on click is triggered all the time
     *
     * @param {BrickContext} $ the olympe context
     * @param {Chart} chart the chart js object used to compute where the user clicked
     * @param {React.MouseEvent} evt the html event on click
     */
    static clickHandler($, chart, evt) {
        const lambda = $.get('On Item Click');
        if(lambda !== null){
            const activePoints = chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, false);
            if(activePoints.length > 0){
                const [{ datasetIndex, index }] = activePoints;

                // Handle the different kind of input we could have
                let inputData = $.get('data');
                let clickedData;
                if (Array.isArray(inputData) || inputData instanceof QueryResult) {
                    if (inputData instanceof QueryResult) {
                        inputData = inputData.toArray();
                    }
                    clickedData = inputData[index];
                } else if (inputData instanceof Map) {
                    let list = inputData.get(chart.data.datasets[datasetIndex].name);
                    if (list instanceof QueryResult) {
                        list = list.toArray();
                    }
                    clickedData = list[index];
                } else if (inputData instanceof Object) {
                    clickedData = inputData[chart.data.datasets[datasetIndex].name][index];
                }

                const [ctrlFlow, data] = lambda.getInputs();
                $.runner(lambda)
                    .set(data, clickedData)
                    .trigger(ctrlFlow);
            } else {
                $.trigger('On Click');
            }
        } else {
            $.trigger('On Click');
        }

    }

}

registerBrick('018d305790f948d1285b', CartesianChart);